import { useState, useEffect } from 'react';
import axios from 'axios';
import BackOfficeMenu from '../components/BackOfficeMenu';
import {
    BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { ProtectedFinancialCard } from '../../components/ProtectedSensitiveData';
import './StockManagement.css';

const API_URL = import.meta.env.VITE_API_URL;

const COLORS = ['#4f46e5', '#8b5cf6', '#f59e0b', '#10b981', '#3b82f6', '#ec4899'];

const StockManagement = ({ onNavigate, onLogout, currentPage }) => {
    const [statistics, setStatistics] = useState(null);
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedView, setSelectedView] = useState('overview'); // overview, alerts, details
    const [filterPeriod, setFilterPeriod] = useState('all'); // all, today, week, month, year, custom
    const [customDateStart, setCustomDateStart] = useState('');
    const [customDateEnd, setCustomDateEnd] = useState('');
    const token = localStorage.getItem('token');

    useEffect(() => {
        fetchStatistics();
        fetchAlerts();
    }, [filterPeriod, customDateStart, customDateEnd]);

    const fetchStatistics = async () => {
        try {
            let url = `${API_URL}/stock/statistics?period=${filterPeriod}`;
            if (filterPeriod === 'custom' && customDateStart && customDateEnd) {
                url += `&startDate=${customDateStart}&endDate=${customDateEnd}`;
            }
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStatistics(response.data.data);
            setLoading(false);
        } catch (error) {
            console.error('Erreur lors de la récupération des statistiques:', error);
            setLoading(false);
        }
    };

    const fetchAlerts = async () => {
        try {
            const response = await axios.get(`${API_URL}/stock/alerts?seuil=10`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAlerts(response.data.data || []);
        } catch (error) {
            console.error('Erreur lors de la récupération des alertes:', error);
        }
    };

    if (loading) {
        return (
            <div className="stk-layout">
                <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
                <div className="stk-content-wrapper">
                    <LoadingSpinner message="Chargement des statistiques du stock..." />
                </div>
            </div>
        );
    }

    if (!statistics) {
        return (
            <div className="stk-layout">
                <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
                <div className="stk-content-wrapper">
                    <h2>Erreur de chargement</h2>
                </div>
            </div>
        );
    }

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('fr-TN', {
            style: 'currency',
            currency: 'TND'
        }).format(value);
    };

    return (
        <div className="stk-layout">
            <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />

            <main className="stk-content-wrapper">
                <div className="stk-container">
                    <header className="stk-header">
                        <div className="stk-header-text">
                            <h1>📊 Gestion du Stock</h1>
                            <p>Analyses en temps réel</p>
                        </div>
                        <nav className="stk-view-tabs">
                            <button className={selectedView === 'overview' ? 'active' : ''} onClick={() => setSelectedView('overview')}>
                                Vue d'ensemble
                            </button>
                            <button className={selectedView === 'alerts' ? 'active' : ''} onClick={() => setSelectedView('alerts')}>
                                Alertes ({alerts.length})
                            </button>
                            <button className={selectedView === 'details' ? 'active' : ''} onClick={() => setSelectedView('details')}>
                                Détails
                            </button>
                        </nav>
                    </header>

                    {/* Barre de Filtres */}
                    <div className="stk-filter-bar">
                        <div className="filter-group">
                            <label><span className="icon">📅</span> Période</label>
                            <select value={filterPeriod} onChange={(e) => setFilterPeriod(e.target.value)}>
                                <option value="all">Mai 2025</option>
                                <option value="today">Aujourd'hui</option>
                                <option value="week">Cette semaine</option>
                            </select>
                        </div>
                        <div className="filter-group">
                            <label><span className="icon">🏷️</span> Type de produit</label>
                            <select>
                                <option>Tous</option>
                                <option>Onduleur</option>
                                <option>Panneaux</option>
                            </select>
                        </div>
                        <div className="search-box">
                            <input type="text" placeholder="Rechercher un produit..." />
                            <span className="search-icon">🔍</span>
                        </div>
                        <button className="btn-export-simple">
                            <span>📥</span> Exporter
                        </button>
                    </div>

                    {selectedView === 'overview' && (
                        <div className="stk-overview-section">

                            {/* ═══════ DUAL MODE GRID : Unités | Linéaire ═══════ */}
                            <div className="stk-dual-mode-grid">

                                {/* ──────────── COLONNE UNITÉS ──────────── */}
                                <div className="mode-section mode-section--unite">
                                    <div className="mode-section-header">
                                        <span className="mode-tag mode-tag--unite">📦 Stock à l'Unité</span>
                                        <span className="mode-summary">
                                            {statistics.unite.resume.nombreProduits} produits · {statistics.unite.resume.quantiteTotale.toLocaleString()} pièces
                                        </span>
                                    </div>

                                    {/* KPI mini-cards */}
                                    <div className="mode-kpi-grid">
                                        <div className="mode-kpi-card">
                                            <div className="mode-kpi-icon mode-kpi-icon--blue">📦</div>
                                            <p>Produits</p>
                                            <h4>{statistics.unite.resume.nombreProduits}</h4>
                                        </div>
                                        <div className="mode-kpi-card">
                                            <div className="mode-kpi-icon mode-kpi-icon--blue">🔢</div>
                                            <p>Pièces en stock</p>
                                            <h4>{statistics.unite.resume.quantiteTotale.toLocaleString()}</h4>
                                        </div>
                                        <ProtectedFinancialCard title="Valeur stock unités" lockedClassName="mode-kpi-card">
                                        <div className="mode-kpi-card">
                                            <div className="mode-kpi-icon mode-kpi-icon--blue">💰</div>
                                            <p>Valeur stock</p>
                                            <h4>{formatCurrency(statistics.unite.resume.valeurTotale)}</h4>
                                        </div>
                                        </ProtectedFinancialCard>
                                        <ProtectedFinancialCard title="CA ventes unités" lockedClassName="mode-kpi-card">
                                        <div className="mode-kpi-card">
                                            <div className="mode-kpi-icon mode-kpi-icon--blue">📈</div>
                                            <p>CA Ventes</p>
                                            <h4>{formatCurrency(statistics.unite.ventes.chiffreAffaires)}</h4>
                                        </div>
                                        </ProtectedFinancialCard>
                                    </div>

                                    {/* Pie : Disponible vs Vendu (pièces) */}
                                    {(() => {
                                        const dispo = statistics.unite.resume.quantiteTotale;
                                        const vendu = statistics.unite.ventes.quantiteVendue || 0;
                                        const total = dispo + vendu;
                                        const pctDispo = total > 0 ? Math.round(dispo / total * 100) : 0;
                                        const pctVendu = 100 - pctDispo;
                                        const data = total > 0
                                            ? [{ name: 'Disponible', value: dispo }, { name: 'Vendu', value: vendu }]
                                            : [{ name: 'Aucune donnée', value: 1 }];
                                        return (
                                            <div className="mode-chart-card">
                                                <h5>Ratio Disponible / Vendu</h5>
                                                <div style={{ position: 'relative' }}>
                                                    <ResponsiveContainer width="100%" height={170}>
                                                        <PieChart>
                                                            <Pie data={data} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={75} stroke="none">
                                                                {total > 0 ? (<><Cell fill="#3b82f6" /><Cell fill="#bfdbfe" /></>) : <Cell fill="#e2e8f0" />}
                                                            </Pie>
                                                            <Tooltip formatter={(v, n) => total > 0 ? [`${v} pièces`, n] : ['-', n]} />
                                                        </PieChart>
                                                    </ResponsiveContainer>
                                                    <div className="pie-center-text">
                                                        {total > 0 ? (<><h4 style={{fontSize:'1.2rem'}}>{pctDispo}%</h4><p>Dispo</p></>) : (<><h4>—</h4><p>Aucun</p></>)}
                                                    </div>
                                                </div>
                                                <div className="chart-legend-horizontal">
                                                    <div className="leg-item"><span className="dot" style={{background:'#3b82f6'}}></span>Dispo {pctDispo}% ({dispo} u.)</div>
                                                    <div className="leg-item"><span className="dot" style={{background:'#bfdbfe'}}></span>Vendu {pctVendu}% ({vendu} u.)</div>
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* Bar : Quantité par type */}
                                    <div className="mode-chart-card">
                                        <h5>Quantité par Type de Produit</h5>
                                        {statistics.unite.produitsParType.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={Math.max(120, statistics.unite.produitsParType.length * 36)}>
                                                <BarChart data={statistics.unite.produitsParType} layout="vertical" margin={{ left: 0, right: 15, top: 5, bottom: 5 }}>
                                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                                                    <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                                                    <YAxis type="category" dataKey="label" width={110} axisLine={false} tickLine={false} tick={{ fill: '#374151', fontSize: 11 }} />
                                                    <Tooltip cursor={{ fill: '#f8fafc' }} formatter={v => [`${v} pièces`, 'Quantité']} />
                                                    <Bar dataKey="quantite" fill="#3b82f6" radius={[0, 6, 6, 0]} barSize={20} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : <p className="mode-empty">Aucun type défini</p>}
                                    </div>

                                    {/* Top 5 liste */}
                                    <div className="mode-list-card">
                                        <h5>🔝 Top 5 — Plus Grand Stock</h5>
                                        <div className="mode-list-rows">
                                            {statistics.unite.topProduits.slice(0, 5).map((p, i) => (
                                                <div key={p._id} className="mode-list-row">
                                                    <span className="mode-rank">{i + 1}</span>
                                                    <div className="mode-list-info">
                                                        <strong>{p.nomProduit}</strong>
                                                        <small>{p.reference || '—'}</small>
                                                    </div>
                                                    <div className="mode-list-right">
                                                        <span className="mode-badge mode-badge--blue">{p.quantite} u.</span>
                                                        <span className="mode-price">{formatCurrency(p.prix)}</span>
                                                    </div>
                                                </div>
                                            ))}
                                            {statistics.unite.topProduits.length === 0 && <p className="mode-empty">Aucun produit</p>}
                                        </div>
                                    </div>

                                    {/* Alertes stock faible */}
                                    <div className="mode-list-card mode-list-card--alert">
                                        <h5>⚠️ Stock Faible (≤ 10 u.)</h5>
                                        <div className="mode-list-rows">
                                            {statistics.unite.alertes.slice(0, 5).map((p, i) => (
                                                <div key={p._id} className="mode-list-row">
                                                    <span className="mode-rank mode-rank--alert">{i + 1}</span>
                                                    <div className="mode-list-info">
                                                        <strong>{p.nomProduit}</strong>
                                                        <small>{p.reference || '—'}</small>
                                                    </div>
                                                    <div className="mode-list-right">
                                                        <span className={`mode-badge ${p.quantite <= 5 ? 'mode-badge--red' : 'mode-badge--orange'}`}>{p.quantite} u.</span>
                                                        <span className="mode-price">{formatCurrency(p.prix)}</span>
                                                    </div>
                                                </div>
                                            ))}
                                            {statistics.unite.alertes.length === 0 && <p className="mode-empty mode-empty--good">✓ Aucune alerte</p>}
                                        </div>
                                    </div>
                                </div>

                                {/* ──────────── COLONNE LINÉAIRE ──────────── */}
                                <div className="mode-section mode-section--lineaire">
                                    <div className="mode-section-header">
                                        <span className="mode-tag mode-tag--lineaire">📐 Stock Linéaire</span>
                                        <span className="mode-summary">
                                            {statistics.lineaire.resume.nombreProduits} produits · {statistics.lineaire.resume.metresTotaux.toFixed(1)} m
                                        </span>
                                    </div>

                                    {/* KPI mini-cards */}
                                    <div className="mode-kpi-grid">
                                        <div className="mode-kpi-card">
                                            <div className="mode-kpi-icon mode-kpi-icon--orange">📐</div>
                                            <p>Produits</p>
                                            <h4>{statistics.lineaire.resume.nombreProduits}</h4>
                                        </div>
                                        <div className="mode-kpi-card">
                                            <div className="mode-kpi-icon mode-kpi-icon--orange">📏</div>
                                            <p>Mètres en stock</p>
                                            <h4>{statistics.lineaire.resume.metresTotaux.toFixed(1)} m</h4>
                                        </div>
                                        <ProtectedFinancialCard title="Valeur stock linéaire" lockedClassName="mode-kpi-card">
                                        <div className="mode-kpi-card">
                                            <div className="mode-kpi-icon mode-kpi-icon--orange">💰</div>
                                            <p>Valeur stock</p>
                                            <h4>{formatCurrency(statistics.lineaire.resume.valeurTotale)}</h4>
                                        </div>
                                        </ProtectedFinancialCard>
                                        <div className="mode-kpi-card">
                                            <div className="mode-kpi-icon mode-kpi-icon--orange">📤</div>
                                            <p>Mètres vendus</p>
                                            <h4>{(statistics.lineaire.ventes.metresVendus || 0).toFixed(1)} m</h4>
                                        </div>
                                    </div>

                                    {/* Pie : Disponible vs Vendu (mètres) */}
                                    {(() => {
                                        const dispo = statistics.lineaire.resume.metresTotaux;
                                        const vendu = statistics.lineaire.ventes.metresVendus || 0;
                                        const total = dispo + vendu;
                                        const pctDispo = total > 0 ? Math.round(dispo / total * 100) : 0;
                                        const pctVendu = 100 - pctDispo;
                                        const data = total > 0
                                            ? [{ name: 'Disponible', value: parseFloat(dispo.toFixed(2)) }, { name: 'Vendu', value: parseFloat(vendu.toFixed(2)) }]
                                            : [{ name: 'Aucune donnée', value: 1 }];
                                        return (
                                            <div className="mode-chart-card">
                                                <h5>Ratio Disponible / Vendu</h5>
                                                <div style={{ position: 'relative' }}>
                                                    <ResponsiveContainer width="100%" height={170}>
                                                        <PieChart>
                                                            <Pie data={data} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={75} stroke="none">
                                                                {total > 0 ? (<><Cell fill="#f59e0b" /><Cell fill="#fde68a" /></>) : <Cell fill="#e2e8f0" />}
                                                            </Pie>
                                                            <Tooltip formatter={(v, n) => total > 0 ? [`${Number(v).toFixed(2)} m`, n] : ['-', n]} />
                                                        </PieChart>
                                                    </ResponsiveContainer>
                                                    <div className="pie-center-text">
                                                        {total > 0 ? (<><h4 style={{fontSize:'1.2rem'}}>{pctDispo}%</h4><p>Dispo</p></>) : (<><h4>—</h4><p>Aucun</p></>)}
                                                    </div>
                                                </div>
                                                <div className="chart-legend-horizontal">
                                                    <div className="leg-item"><span className="dot" style={{background:'#f59e0b'}}></span>Dispo {pctDispo}% ({dispo.toFixed(1)} m)</div>
                                                    <div className="leg-item"><span className="dot" style={{background:'#fde68a'}}></span>Vendu {pctVendu}% ({vendu.toFixed(1)} m)</div>
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* Bar : Mètres par type */}
                                    <div className="mode-chart-card">
                                        <h5>Mètres par Type de Produit</h5>
                                        {statistics.lineaire.produitsParType.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={Math.max(120, statistics.lineaire.produitsParType.length * 36)}>
                                                <BarChart data={statistics.lineaire.produitsParType} layout="vertical" margin={{ left: 0, right: 15, top: 5, bottom: 5 }}>
                                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                                                    <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                                                    <YAxis type="category" dataKey="label" width={110} axisLine={false} tickLine={false} tick={{ fill: '#374151', fontSize: 11 }} />
                                                    <Tooltip cursor={{ fill: '#f8fafc' }} formatter={v => [`${Number(v).toFixed(2)} m`, 'Mètres']} />
                                                    <Bar dataKey="metres" fill="#f59e0b" radius={[0, 6, 6, 0]} barSize={20} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div className="mode-no-data">
                                                <span style={{fontSize:'2rem'}}>📐</span>
                                                <p>Aucun produit linéaire enregistré</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Top 5 liste (mètres) */}
                                    <div className="mode-list-card">
                                        <h5>🔝 Top 5 — Plus de Mètres</h5>
                                        <div className="mode-list-rows">
                                            {statistics.lineaire.topProduits.slice(0, 5).map((p, i) => (
                                                <div key={p._id} className="mode-list-row">
                                                    <span className="mode-rank">{i + 1}</span>
                                                    <div className="mode-list-info">
                                                        <strong>{p.nomProduit}</strong>
                                                        <small>{p.reference || '—'}</small>
                                                    </div>
                                                    <div className="mode-list-right">
                                                        <span className="mode-badge mode-badge--orange">{p.metres.toFixed(1)} m</span>
                                                        <span className="mode-price">{formatCurrency(p.prixMetre)}/m</span>
                                                    </div>
                                                </div>
                                            ))}
                                            {statistics.lineaire.topProduits.length === 0 && <p className="mode-empty">Aucun produit linéaire</p>}
                                        </div>
                                    </div>

                                    {/* Alertes mètres faibles */}
                                    <div className="mode-list-card mode-list-card--alert">
                                        <h5>⚠️ Mètres Faibles (≤ 50 m)</h5>
                                        <div className="mode-list-rows">
                                            {statistics.lineaire.alertes.slice(0, 5).map((p, i) => (
                                                <div key={p._id} className="mode-list-row">
                                                    <span className="mode-rank mode-rank--alert">{i + 1}</span>
                                                    <div className="mode-list-info">
                                                        <strong>{p.nomProduit}</strong>
                                                        <small>{p.reference || '—'}</small>
                                                    </div>
                                                    <div className="mode-list-right">
                                                        <span className={`mode-badge ${p.metres <= 10 ? 'mode-badge--red' : 'mode-badge--orange'}`}>{p.metres.toFixed(1)} m</span>
                                                        <span className="mode-price">{formatCurrency(p.prixMetre)}/m</span>
                                                    </div>
                                                </div>
                                            ))}
                                            {statistics.lineaire.alertes.length === 0 && <p className="mode-empty mode-empty--good">✓ Aucune alerte</p>}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ═══════ SECTION COMMUNE — CA Global + Top Vendus + Distribution ═══════ */}
                            <section className="stk-bottom-section">

                                {/* CA Global */}
                                <ProtectedFinancialCard title="CA global protégé" lockedClassName="stk-ca-global-card">
                                <div className="stk-ca-global-card">
                                    <div className="ca-icon">💹</div>
                                    <div className="ca-main">
                                        <p>Chiffre d'affaires total</p>
                                        <h3>{formatCurrency(statistics.ventes.chiffreAffairesTotal)}</h3>
                                    </div>
                                    <div className="ca-breakdown">
                                        <span><span className="ca-dot ca-dot--blue"></span>Unités : {formatCurrency(statistics.unite.ventes.chiffreAffaires)}</span>
                                        <span><span className="ca-dot ca-dot--orange"></span>Linéaire : {formatCurrency(statistics.lineaire.ventes.chiffreAffaires)}</span>
                                    </div>
                                </div>
                                </ProtectedFinancialCard>

                                {/* Top produits vendus */}
                                {statistics.produitsVendus && statistics.produitsVendus.length > 0 && (
                                    <div className="chart-card-v2">
                                        <h3>💰 Top Produits Vendus (CA)</h3>
                                        <div className="item-rows-container" style={{marginTop: 0}}>
                                            {statistics.produitsVendus.slice(0, 8).map((p, i) => (
                                                <div key={p._id || i} className="premium-item-row">
                                                    <div className="row-left">
                                                        <span className="circle-badge green">{i + 1}</span>
                                                        <div className="name-box">
                                                            <strong>{p.nomProduit}</strong>
                                                            <small>
                                                                {p.modeGestionStock === 'lineaire'
                                                                    ? `${(p.metresVendus || 0).toFixed(1)} m vendus`
                                                                    : `${p.quantiteVendue} u. vendues`
                                                                }
                                                                <span className={`mode-inline-tag ${p.modeGestionStock === 'lineaire' ? 'orange' : 'blue'}`}>
                                                                    {p.modeGestionStock === 'lineaire' ? '📐' : '📦'}
                                                                </span>
                                                            </small>
                                                        </div>
                                                    </div>
                                                    <ProtectedFinancialCard title="CA produit" lockedClassName="row-right">
                                                    <div className="row-right">
                                                        <span className="qty-badge green">{formatCurrency(p.chiffreAffaires)}</span>
                                                    </div>
                                                    </ProtectedFinancialCard>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Distribution des prix — Unités */}
                                {statistics.unite.distributionPrix.length > 0 && (
                                    <div className="chart-card-v2">
                                        <h3>Distribution des Prix — Unités (DT/pièce)</h3>
                                        <div className="chart-wrapper">
                                            <ResponsiveContainer width="100%" height={230}>
                                                <BarChart data={statistics.unite.distributionPrix}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                    <XAxis dataKey="range" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                                                    <Tooltip cursor={{ fill: '#f8fafc' }} formatter={v => [`${v} produits`, 'Nombre']} />
                                                    <Bar dataKey="count" fill="#3b82f6" radius={[6, 6, 0, 0]} barSize={45} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                )}

                                {/* Distribution des prix — Linéaire */}
                                {statistics.lineaire.distributionPrix.length > 0 && (
                                    <div className="chart-card-v2">
                                        <h3>Distribution des Prix — Linéaire (DT/m)</h3>
                                        <div className="chart-wrapper">
                                            <ResponsiveContainer width="100%" height={230}>
                                                <BarChart data={statistics.lineaire.distributionPrix}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                                    <XAxis dataKey="range" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                                                    <Tooltip cursor={{ fill: '#f8fafc' }} formatter={v => [`${v} produits`, 'Nombre']} />
                                                    <Bar dataKey="count" fill="#f59e0b" radius={[6, 6, 0, 0]} barSize={45} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                )}
                            </section>

                            <footer className="stk-dashboard-footer">
                                <div className="footer-left">
                                    <span>🕒 Données calculées en temps réel</span>
                                </div>
                                <div className="footer-right">
                                    <span className="status-label">Modes actifs :</span>
                                    <span className="status-badge-green">📦 Unités + 📐 Linéaire</span>
                                </div>
                            </footer>
                        </div>
                    )}

                    {/* Vue Alertes */}

                    {selectedView === 'alerts' && (
                        <div className="stk-alerts-view">
                            <div className="alerts-header-actions">
                                <div className="alerts-title-box">
                                    <span className="alert-icon">🔔</span>
                                    <div>
                                        <h2>Alertes de stock faible</h2>
                                        <p>Produits avec un stock ≤ 10 unités</p>
                                    </div>
                                </div>
                                <div className="alerts-actions-buttons">
                                    <button className="btn-refresh" onClick={fetchAlerts}>
                                        <span>🔄</span> Actualiser
                                    </button>
                                    <button className="btn-export">
                                        <span>📥</span> Exporter
                                    </button>
                                </div>
                            </div>

                            <div className="alerts-mini-stats">
                                <div className="mini-stat-card">
                                    <p>Total alertes</p>
                                    <h3 className="total">{alerts.length}</h3>
                                </div>
                                <div className="mini-stat-card">
                                    <p>Stock critique (≤ 5)</p>
                                    <h3 className="critical">{alerts.filter(a => a.quantite <= 5).length}</h3>
                                </div>
                                <div className="mini-stat-card">
                                    <p>Stock faible (6-10)</p>
                                    <h3 className="warning">{alerts.filter(a => a.quantite > 5).length}</h3>
                                </div>
                            </div>

                            <div className="alerts-table-container">
                                <table className="alerts-table">
                                    <thead>
                                        <tr>
                                            <th>Produit</th>
                                            <th>Référence</th>
                                            <th>Type</th>
                                            <th>Marque</th>
                                            <th>Stock</th>
                                            <th>Prix (DT)</th>
                                            <th>Statut</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {alerts.length > 0 ? (
                                            alerts.map((a) => (
                                                <tr key={a._id}>
                                                    <td className="product-name">{a.nomProduit}</td>
                                                    <td>{a.reference}</td>
                                                    <td>{a.typeProduit?.label || 'N/A'}</td>
                                                    <td>{a.marque?.name || 'N/A'}</td>
                                                    <td className="stock-count">
                                                        <span className={a.quantite <= 5 ? 'critical' : 'warning'}>
                                                            {a.quantite}
                                                        </span>
                                                    </td>
                                                    <td>{formatCurrency(a.prix)}</td>
                                                    <td>
                                                        <span className={`status-badge ${a.quantite <= 5 ? 'critical' : 'warning'}`}>
                                                            {a.quantite <= 5 ? 'Critique' : 'Faible'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="empty-row">Aucune alerte de stock détectée</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {selectedView === 'details' && (
                        <div className="details-section">
                            <h2>📊 Résumé Détaillé</h2>
                            <div className="details-card">
                                <ProtectedFinancialCard title="Valeur totale protégée" lockedClassName="sdl-inline-lock-card">
                                <p>Valeur Totale: {formatCurrency(statistics.resume.valeurTotaleStock)}</p>
                                </ProtectedFinancialCard>
                                <p>Produits: {statistics.resume.nombreTotalProduits}</p>
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default StockManagement;
